/**
 * Клиенты агентства.
 *
 * Это тот экран, ради которого агентство и покупает Партнёра: все клиенты в
 * одной панели, квота двигается между ними, отчёт собирается под брендом
 * агентства.
 *
 * Три вещи, которые определяют вёрстку.
 *
 * 1. **Квота покупается на организацию и раздаётся пространствам** (спека §5).
 *    Поэтому здесь не «сколько куплено клиенту», а «сколько ему выделено из
 *    общего»: остаток организации показан отдельной строкой и считается
 *    сервером, а не экраном — но виден до нажатия, иначе выделение вслепую
 *    упирается в `409` и выглядит как поломка.
 * 2. **Переключение на клиента — это смена области, а не открытие карточки.**
 *    Агентство обязано видеть, в чьих данных находится: выбор уезжает в тот же
 *    переключатель контекста наверху, и дальше все экраны показывают этого
 *    клиента.
 * 3. **Изоляция клиентов — на стороне базы.** Экран ничего не фильтрует руками:
 *    он показывает то, что отдал `GET /agency/clients`. Любая фильтрация «по
 *    последним N записям» на клиенте — это ошибка проектирования, которая
 *    однажды покажет одному клиенту данные другого.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog } from '../ui/Dialog';
import { Confirm, TextField } from '../ui/fields';
import { Empty, ErrorNotice, Skeleton } from '../ui/state';
import { useAttempt } from '../ui/attempt';
import { useScope } from '../scope';
import { useScrollToTop } from '../ui/scroll';
import { useAccess } from '../session';
import { sectionFor } from '../sections';
import { idempotencyKey } from '../api/client';
import {
  agencyClientBody,
  BLANK_CLIENT,
  clientDraftIsReady,
  freeQuota,
  NAME_MAX,
  refusalOf,
  spacesOutsideClients,
  trackedError,
  type ClientDraft,
} from './agency-create';
import {
  ClientHasBrandsBody,
  ClientRemoveBody,
  clientArchiveOnly,
  clientRemoveDialog,
  openClientBrands,
  removeClient,
} from './remove-client';
import {
  createAgencyClient,
  createAgencyReport,
  getWorkspaceAllocation,
  listAgencyClients,
  listWorkspaces,
  updateAllocation,
  type AgencyClient,
} from '../api/team';
import { getOrganizationQuotas } from '../api/team';
import {
  countOf,
  coverageOf,
  dateProse,
  deltaPoints,
  int,
  NOT_MEASURED,
  score,
  statusLadder,
} from '@shared/src/format';

type Load =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      clients: AgencyClient[];
      /** Куплено организацией. */
      tracked: number;
      /**
       * Выделения ВСЕХ пространств организации, а не одних клиентских: ровно
       * их сервер и складывает, отбивая `allocation_exceeds_quota`.
       */
      allocated: number[];
    }
  | { kind: 'failed'; error: unknown };

/** Прошлый календарный месяц — обычный период клиентского отчёта. */
function lastMonth(): { from: string; to: string; title: string } {
  const today = new Date();
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));
  const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  const day = (d: Date) => d.toISOString().slice(0, 10);
  return {
    from: day(start),
    to: day(end),
    title: `${dateProse(start)} — ${dateProse(end)}`,
  };
}

export function Clients() {
  const scope = useScope();
  const access = useAccess();
  const navigate = useNavigate();

  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  /**
   * Занятые действия — множеством, а не одной строкой.
   *
   * Одна строка держит ровно одно действие: пока летит удаление одного клиента,
   * человек успевает начать удаление второго, первое возвращается и снимает
   * занятость СО ВТОРОГО. Кнопка второго оживает под летящим запросом, и второй
   * щелчок приносит 409 поверх удавшегося удаления. Ключи независимы — значит и
   * занятость у каждого своя.
   */
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  const [failure, setFailure] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  /**
   * Сколько раз экрану было что сказать — ключ возврата к началу.
   *
   * Строка «Готово» стоит наверху экрана, а нажимают внизу: карточка клиента
   * исчезает прямо под курсором, а подтверждение появляется там, куда человек в
   * этот момент не смотрит. Для него это молчаливое исчезновение, то есть сбой.
   *
   * Считается именно ПОЯВЛЕНИЕ строки, а не её состояние: `act` гасит прошлую
   * строку перед новой отправкой, и дёргать экран на каждом нажатии незачем.
   * Плавность спрашивается у системы — этим занимается сам `useScrollToTop`.
   */
  const [said, setSaid] = useState(0);
  useScrollToTop(said);

  const [editing, setEditing] = useState<AgencyClient | null>(null);
  const [tracked, setTracked] = useState(0);

  /**
   * Клиент, которого собрались удалить, — один на оба окна.
   *
   * Какое из двух показать, решает `clientRemoveDialog`, а не два разных
   * состояния: два флага рано или поздно оказываются поднятыми одновременно, и
   * поверх ведущего окна встаёт окно с красной кнопкой — ровно то, от которого
   * ведущее и защищает.
   */
  const [removing, setRemoving] = useState<AgencyClient | null>(null);

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<ClientDraft>(BLANK_CLIENT);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [made, setMade] = useState<{
    client: AgencyClient;
    invited: string | null;
  } | null>(null);

  /**
   * Ключ идемпотентности заведения — на попытку, а не на нажатие.
   *
   * Он уже существует к моменту, когда окно открывается, живёт через все
   * повторы внутри него и обновляется на закрытии — то есть один и тот же ключ
   * несут и первая отправка, и повтор после обрыва связи. Создавать ключ в
   * обработчике нажатия здесь нельзя вдвойне: второй запрос заводит клиенту
   * ВТОРОЕ пространство, оно занимает место в тарифе, и убрать его из кабинета
   * нечем — удаления пространств в продукте нет.
   */
  const createKey = useAttempt();

  useEffect(() => {
    const abort = new AbortController();
    // Квоты берутся из `/organizations/current/quotas`, а не из биллингового
    // портала: портал открыт только владельцу, а этот экран ведёт и аналитик —
    // раздача квоты между клиентами его работа.
    //
    // Пространства берутся отдельно и не ради списка: остаток общей квоты
    // считается по выделениям ВСЕХ пространств. Внутреннее пространство
    // агентства в клиентский список не попадает, а его выделение сервер в свою
    // сумму берёт — экран, считающий остаток по клиентам, обещает больше, чем
    // сервер отдаст, и упирается в `409` на числе, которое сам же и предложил.
    // Отдельного «сколько роздано всего» контракт не отдаёт: до тех пор, пока
    // его не появится, складываем сами и по тем же строкам, что и сервер.
    Promise.all([
      listAgencyClients(abort.signal),
      getOrganizationQuotas(abort.signal),
      listWorkspaces(abort.signal),
    ])
      .then(async ([clients, quotas, workspaces]) => {
        const outside = await Promise.all(
          spacesOutsideClients(workspaces.items, clients.items).map((id) =>
            getWorkspaceAllocation(id, abort.signal),
          ),
        );
        setLoad({
          kind: 'ready',
          clients: clients.items,
          tracked: quotas.tracked_questions,
          allocated: [
            ...clients.items.map((client) => client.allocation?.tracked_questions ?? 0),
            ...outside.map((allocation) => allocation?.tracked_questions ?? 0),
          ],
        });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoad({ kind: 'failed', error });
      });
    return () => abort.abort();
  }, [attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  /* Занятость снимается и ставится по одному ключу: действия экрана независимы,
     и завершение одного не имеет права оживить кнопку другого. */
  const startBusy = useCallback(
    (key: string) => setBusy((keys) => new Set(keys).add(key)),
    [],
  );
  const endBusy = useCallback(
    (key: string) =>
      setBusy((keys) => {
        const rest = new Set(keys);
        rest.delete(key);
        return rest;
      }),
    [],
  );

  const act = useCallback(
    async (key: string, run: () => Promise<string>) => {
      startBusy(key);
      setFailure(null);
      setNote(null);
      try {
        setNote(await run());
        setSaid((times) => times + 1);
        reload();
      } catch (error) {
        setFailure(error);
      } finally {
        endBusy(key);
      }
    },
    [reload, startBusy, endBusy],
  );

  /**
   * Открыть клиента: смена области целиком, а не переход в карточку.
   *
   * У клиента без брендов открывать нечего, и уводить его на обзор нельзя:
   * экран честно скажет «выберите бренд», а выбирать будет не из чего. Такой
   * клиент — обычное состояние, а не редкость: ровно таким он и появляется на
   * свет, пока ему не завели бренд. Поэтому область меняется так же, а ведёт
   * кнопка туда, где работа продолжается.
   */
  function openClient(client: AgencyClient) {
    scope.selectWorkspace(client.workspace_id);
    const first = client.brands[0];
    if (first) scope.selectBrand(first.brand_id);
    navigate(first ? '/' : '/brands/new');
  }

  function openCreate() {
    setRefusal(null);
    setMade(null);
    setCreating(true);
  }

  /**
   * Попытка кончилась — следующая пойдёт с новым ключом.
   *
   * «Кончилась» решает не это место, а сам ключ (`ui/attempt.ts`): если исход
   * последней отправки неизвестен — запрос ушёл, а ответа не было, — просьба
   * не выполняется, и повтор уйдёт с тем же ключом. Иначе Escape на висящем
   * запросе заводил бы второго клиента: сервер тем временем мог закоммитить.
   */
  function closeCreate() {
    setCreating(false);
    setRefusal(null);
    createKey.renew();
  }

  /** Прошлая попытка всё-таки прошла: клиент есть, а список о нём не знает. */
  function acceptRepeat() {
    setDraft(BLANK_CLIENT);
    closeCreate();
    scope.reload();
    reload();
    setNote('Прошлая попытка всё-таки дошла: клиент заведён, список обновлён.');
  }

  async function submitCreate(event: FormEvent) {
    event.preventDefault();
    startBusy('client');
    setRefusal(null);
    setNote(null);
    try {
      const client = await createKey.send((key) =>
        createAgencyClient(agencyClientBody(draft), key),
      );
      const invited = draft.inviteEmail.trim() || null;
      setDraft(BLANK_CLIENT);
      closeCreate();
      /* Область перечитывается и сразу переключается на нового клиента, и это
         не удобство. Следующий шаг — завести ему бренд, а бренд заводится в
         ВЫБРАННОЕ пространство: без переключения он ушёл бы к тому клиенту, на
         котором агентство стояло до этого, и съел бы его квоту. */
      scope.reload();
      scope.selectWorkspace(client.workspace_id);
      setMade({ client, invited });
      reload();
    } catch (error) {
      setRefusal(error);
    } finally {
      endBusy('client');
    }
  }

  // Заводить клиентов может владелец и аналитик — так же, как читать список.
  const canCreate = access.reaches('POST', '/agency/clients');
  /**
   * Дотягивается ли роль до раздела «Тариф».
   *
   * Спрашивается у `sections.ts`, а не своим `reaches('GET', '/billing/portal')`:
   * список ролей, переписанный рядом с кнопкой, — вторая копия правды, и
   * расходится она всегда в сторону «показали ссылку, за которой отказ».
   * Заводить клиентов может и аналитик, а `/billing/**` по матрице открыт
   * только владельцу: ссылка на тариф уводила бы аналитика в «раздел закрыт».
   */
  const plan = sectionFor('/plan');
  const canOpenPlan = plan ? access.reaches(plan.needs.method, plan.needs.path) : false;
  /** Отправка ушла: пока ответа нет, попытка не кончилась и окно не закрывается. */
  const sending = busy.has('client');

  const head = (
    <div className="screen-head">
      <div>
        <h1>Клиенты</h1>
        <p className="stamp">
          Каждый клиент — своё пространство. Данные клиентов не пересекаются
        </p>
      </div>
      {canCreate && load.kind === 'ready' ? (
        <button className="btn btn-primary btn-sm" type="button" onClick={openCreate}>
          Добавить клиента
        </button>
      ) : null}
    </div>
  );

  if (load.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }
  if (load.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice
          error={load.error}
          action={
            <button className="btn btn-ghost btn-sm" type="button" onClick={reload}>
              Повторить
            </button>
          }
        />
      </>
    );
  }

  const { clients } = load;
  // Выделение квоты — право из матрицы контракта: PUT открыт
  // владельцу, чтение — ещё и аналитику с наблюдателем.
  const canAllocate = access.reaches('PUT', '/workspaces/{workspaceId}/allocation');
  // Убрать клиента может тот же, кто заводит: владелец и аналитик. Спрашивается
  // ровно тот путь контракта, которым уйдёт запрос, — опечатка в шаблоне молча
  // провалилась бы в глобовое правило `/workspaces/**` и открыла бы кнопку там,
  // где сервер откажет.
  const canRemove = access.reaches('POST', '/workspaces/{workspaceId}/archive');
  const free = freeQuota(load.tracked, load.allocated);
  const period = lastMonth();

  /** Какое из двух окон удаления показано: ведущее или подтверждение. */
  const removeMode = removing ? clientRemoveDialog(removing) : null;

  /**
   * Подтверждение удаления.
   *
   * Окно закрывается ПЕРВОЙ строкой, как и в архивации бренда: у `Confirm` нет
   * ни `busy`, ни `disabled`, и второй щелчок по той же кнопке ушёл бы вторым
   * запросом — то есть отказом `409` поверх удавшегося удаления.
   */
  function confirmRemove() {
    const client = removing;
    setRemoving(null);
    if (!client) return;
    void act(`remove-${client.workspace_id}`, () =>
      removeClient(client, {
        // Спрашивается ОБЛАСТЬ, а не этот экран: ответ приходит позже, и к тому
        // времени экран может быть уже другим — область живёт выше всех.
        selectedWorkspaceId: scope.currentWorkspaceId,
        dropSelection: () => scope.selectWorkspace(null),
        reload: () => scope.reload(),
      }),
    );
  }

  /** Выход из ведущего окна: область переезжает к клиенту, экран — к брендам. */
  function goToClientBrands() {
    const client = removing;
    setRemoving(null);
    if (!client) return;
    openClientBrands(client, {
      selectWorkspace: (workspaceId) => scope.selectWorkspace(workspaceId),
      navigate: (path) => navigate(path),
    });
  }

  /**
   * Ход в архив клиента: тот же переезд области, но экран открывается архивом.
   *
   * Переход один и тот же (`openClientBrands`), и разъехаться этим двум местам
   * нельзя: смена области и уход на экран — одна операция, иначе человек
   * оказывается у брендов чужого пространства. Отличается только состояние
   * перехода: кнопка обещает архив, значит открыться обязан архив, а не вкладка
   * «В работе», на которой у этого клиента пусто по определению.
   */
  function goToClientArchive(client: AgencyClient) {
    openClientBrands(client, {
      selectWorkspace: (workspaceId) => scope.selectWorkspace(workspaceId),
      navigate: (path) => navigate(path, { state: { view: 'archived' } }),
    });
  }

  /* --- заведение клиента: окно и сообщение об удавшемся --- */

  const createDialog = (
    <Dialog
      open={creating}
      title="Новый клиент"
      /* Пока запрос в полёте, окно не закрывается ни Escape, ни отменой:
         закрытие означает «попытка кончилась», а она не кончилась — сервер
         может прямо сейчас заводить пространство, которое из кабинета потом не
         удалить. Ключ это переживает и сам (`ui/attempt.ts`), но обещать
         человеку отмену того, что уже ушло, всё равно нельзя. */
      onClose={() => {
        if (!sending) closeCreate();
      }}
      footer={
        <>
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            disabled={sending}
            onClick={closeCreate}
          >
            Отмена
          </button>
          <button
            className="btn btn-primary btn-sm"
            type="submit"
            form="client-new"
            disabled={sending || !clientDraftIsReady(draft)}
          >
            {sending ? 'Заводим…' : 'Завести клиента'}
          </button>
        </>
      }
    >
      <p className="dlg-lead">
        Клиенту создаётся своё пространство: свои бренды, своя доля квоты, свой доступ.
        Бренд заводится следующим шагом — по адресу сайта.
      </p>

      {sending ? (
        <p className="stamp" role="status">
          Заводим клиента. Окно закроется само — отменять уже нечего: запрос ушёл на
          сервер.
        </p>
      ) : null}

      {refusal ? (
        <CreateRefusal
          error={refusal}
          free={free}
          canOpenPlan={canOpenPlan}
          onRepeat={acceptRepeat}
        />
      ) : null}

      <form id="client-new" onSubmit={submitCreate} noValidate>
        <TextField
          label="Название клиента"
          value={draft.name}
          onChange={(value) => setDraft({ ...draft, name: value.slice(0, NAME_MAX) })}
          placeholder="Автоцентр Прайд"
          hint="Как вы называете его внутри агентства"
        />
        <TextField
          label="Имя для самого клиента"
          value={draft.displayName}
          onChange={(value) =>
            setDraft({ ...draft, displayName: value.slice(0, NAME_MAX) })
          }
          optional
          hint="Так клиент увидит себя в отчётах. Не заполните — возьмём название выше"
        />
        <TextField
          label="Почта для приглашения"
          value={draft.inviteEmail}
          onChange={(value) => setDraft({ ...draft, inviteEmail: value })}
          type="email"
          optional
          hint="Позовём его в кабинет сразу. Он увидит только свой бренд"
        />
        {/* Поле обычное текстовое, а введённое не «чистится» от лишних знаков.
            Вычищение цифр молча превращало «12.5» в 125, а «1e3» — в 13:
            десятикратно другое выделение квоты, отнятой у остальных клиентов.
            `type="number"` тут тоже не помощник — он такие строки отдаёт как
            есть. Синтаксис проверяется целиком (`trackedError`), и несогласие
            видно человеку строкой под полем, а не подставляется за него. */}
        <TextField
          label="Вопросов в квоту"
          value={draft.tracked}
          onChange={(value) => setDraft({ ...draft, tracked: value })}
          inputMode="numeric"
          optional
          error={trackedError(draft.tracked)}
          hint={`Свободно ${countOf(Math.max(0, free), 'вопрос', 'вопроса', 'вопросов')}. Можно выделить и позже`}
        />
      </form>
    </Dialog>
  );

  /**
   * Сказанное о случившемся — одно на оба выхода экрана.
   *
   * Выходов два: список карточек и пустое состояние. Строка, стоявшая только в
   * первом, исчезала вместе с последним клиентом — человек удалял единственного
   * и не получал ни слова, ровно в том случае, где исчезновение всего экрана
   * пугает сильнее всего. Собирается один раз и рисуется в обоих местах.
   */
  const done = note ? (
    <p className="notice ok" role="status">
      <b>Готово</b>
      {note}
    </p>
  ) : null;

  const madeNotice = made ? (
    <p className="notice ok" role="status">
      <b>Клиент заведён</b>
      {/* Имя без кавычек: клиента зовут «Мебель „Дуб и клён“», и ёлочки вокруг
          такого имени дают вложенные кавычки прямо в первой строке. Тире тут
          и так отделяет название от сказанного о нём. */}
      {`${made.client.client_display_name || made.client.name} — пространство создано и выбрано. `}
      {made.invited ? `Приглашение ушло на ${made.invited}. ` : ''}
      Бренда в нём ещё нет: заведём по адресу сайта.
      <span className="notice-act">
        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={() => navigate('/brands/new')}
        >
          Завести бренд
        </button>
      </span>
    </p>
  ) : null;

  if (clients.length === 0) {
    return (
      <>
        {head}
        {madeNotice}
        {done}
        <Empty
          eyebrow="Клиентов ещё нет"
          title="Ни одного клиентского пространства"
          action={
            canCreate ? (
              <button className="btn btn-primary" type="button" onClick={openCreate}>
                Добавить клиента
              </button>
            ) : null
          }
        >
          Клиент агентства — это отдельное пространство со своими брендами, своей долей
          общей квоты и своим доступом. Он видит только свой бренд и не знает о
          существовании Seeno.
        </Empty>
        {createDialog}
      </>
    );
  }

  return (
    <>
      {head}

      {failure ? <ErrorNotice error={failure} /> : null}
      {madeNotice}
      {done}

      <section className="summary">
        <div>
          <div className="value tnum">{int(clients.length)}</div>
          <div className="status">
            {countOf(clients.length, 'клиент', 'клиента', 'клиентов')}
          </div>
        </div>
        <div>
          <div className="h3 tnum">{int(free)}</div>
          <div className="stamp">не выделено из квоты</div>
        </div>
      </section>

      <div className="cards">
        {clients.map((client) => {
          const allocation = client.allocation;
          const attention = client.brands.filter((b) => b.needs_attention).length;
          /* Архив считает область: полный список брендов организации живёт
             там, и рабочая поверхность его не читает — иначе архивный бренд
             однажды снова окажется в счётчике или в переключателе. */
          const archived = scope.archivedCountIn(client.workspace_id);
          return (
            <article key={client.workspace_id} className="client">
              <div className="client-head">
                <div>
                  <h2>{client.client_display_name || client.name}</h2>
                  {/* Архив назван числом рядом с живыми: без него карточка
                      клиента, у которого всё убрано из работы, показывает
                      «0 брендов» — и кнопка, ведущая в архив, выглядит
                      обещанием пустоты. */}
                  <p className="stamp">
                    {countOf(client.brands.length, 'бренд', 'бренда', 'брендов')} ·{' '}
                    {archived > 0 ? `${int(archived)} в архиве · ` : ''}
                    {countOf(
                      client.members_count ?? 0,
                      'человек',
                      'человека',
                      'человек',
                    )}{' '}
                    с доступом
                    {client.portal_hostname ? ` · портал ${client.portal_hostname}` : ''}
                  </p>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  onClick={() => openClient(client)}
                >
                  {client.brands.length > 0 ? 'Открыть обзор бренда' : 'Завести бренд'}
                </button>
              </div>

              {attention > 0 ? (
                <p className="client-flag">
                  {countOf(
                    attention,
                    'бренд требует',
                    'бренда требуют',
                    'брендов требуют',
                  )}{' '}
                  внимания
                </p>
              ) : null}

              <ul className="client-brands">
                {client.brands.map((brand) => (
                  <li key={brand.brand_id}>
                    <span className="cb-name">{brand.brand_name}</span>
                    <span
                      className={`cb-score tnum${
                        brand.index_score === null || brand.index_score === undefined
                          ? ' na'
                          : ''
                      }`}
                    >
                      {brand.index_score === null || brand.index_score === undefined
                        ? NOT_MEASURED
                        : score(brand.index_score)}
                    </span>
                    <span className="cb-status">
                      {brand.index_score === null || brand.index_score === undefined
                        ? brand.active_run_state === 'running'
                          ? 'идёт первый замер'
                          : 'замера ещё не было'
                        : statusLadder(brand.index_score)}
                    </span>
                    <span className="cb-delta">
                      {brand.index_delta === null || brand.index_delta === undefined ? (
                        <span className="stamp">не с чем сравнить</span>
                      ) : (
                        <span
                          className={`pill ${brand.index_delta > 0 ? 'up' : brand.index_delta < 0 ? 'down' : 'flat'}`}
                        >
                          {deltaPoints(brand.index_delta, 1)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="stamp client-basis">
                {client.brands[0]?.last_snapshot_date
                  ? `Замер на ${dateProse(client.brands[0].last_snapshot_date)}`
                  : 'Замеров ещё не было'}
                {allocation
                  ? ` · выделено ${coverageOf(allocation.tracked_used ?? 0, allocation.tracked_questions, 'вопрос', 'вопроса', 'вопросов')}`
                  : ' · квота не выделена'}
              </p>

              <div className="row-actions">
                {/* Ход в архив — только там, где иначе до него не дойти:
                    живых брендов не осталось, а в архиве что-то лежит.
                    Переключатель наверху таких клиентов не показывает вовсе
                    (пространство без живых брендов не даёт группы), а кнопка
                    «Удалить» архивного бренда живёт во вкладке «Архив» — то
                    есть за дверью, к которой не было ручки. Прав он не
                    спрашивает: это переход, а не действие, и сам раздел
                    закрыт матрицей выше. */}
                {clientArchiveOnly(client, archived) ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => goToClientArchive(client)}
                  >
                    Открыть архив брендов
                  </button>
                ) : null}
                {canAllocate ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => {
                      setEditing(client);
                      setTracked(allocation?.tracked_questions ?? 0);
                    }}
                  >
                    Изменить квоту
                  </button>
                ) : null}
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  disabled={busy.has(`report-${client.workspace_id}`)}
                  onClick={() =>
                    act(`report-${client.workspace_id}`, async () => {
                      await createAgencyReport(
                        client.workspace_id,
                        client.brands[0]?.brand_id ?? null,
                        period.from,
                        period.to,
                        idempotencyKey(),
                      );
                      return `Отчёт за ${period.title} собирается. Он появится в центре отчётов — под вашим брендом, без упоминания Seeno.`;
                    })
                  }
                >
                  Отчёт клиенту за месяц
                </button>
                {canRemove ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    disabled={busy.has(`remove-${client.workspace_id}`)}
                    onClick={() => setRemoving(client)}
                  >
                    Удалить клиента
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      {/* --- перераспределение квоты --- */}
      <Dialog
        open={editing !== null}
        title="Квота клиента"
        onClose={() => setEditing(null)}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setEditing(null)}
            >
              Отмена
            </button>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={busy.has('allocation')}
              onClick={() => {
                const client = editing;
                setEditing(null);
                if (!client) return;
                void act('allocation', async () => {
                  await updateAllocation(client.workspace_id, tracked);
                  // Без ёлочек вокруг имени по той же причине, что и в
                  // сообщении о заведённом клиенте: имя клиента само бывает в
                  // кавычках, и вторая пара вокруг него — вложенные кавычки.
                  return `Клиенту ${client.client_display_name || client.name} выделили ${countOf(tracked, 'вопрос', 'вопроса', 'вопросов')}.`;
                });
              }}
            >
              Выделить квоту
            </button>
          </>
        }
      >
        <p className="dlg-lead">
          Свободно {countOf(free, 'вопрос', 'вопроса', 'вопросов')}. Больше купленного
          выделить нельзя.
        </p>

        <div className="field">
          <label htmlFor="alloc-tracked">Отслеживаемых вопросов</label>
          <input
            id="alloc-tracked"
            type="number"
            min={0}
            max={load.tracked}
            value={tracked}
            onChange={(event) => setTracked(Number(event.target.value))}
          />
          <span className="hint">
            Клиент увидит ровно столько отслеживаемых вопросов, сколько вы выделили
          </span>
        </div>
      </Dialog>

      {/* --- удаление клиента: подтверждение и ведущее окно --- */}

      <Confirm
        open={removeMode === 'confirm'}
        title="Удалить клиента"
        action="Удалить клиента"
        danger
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      >
        {removing ? <ClientRemoveBody client={removing} /> : null}
      </Confirm>

      {/* Пока у клиента есть живые бренды, сервер архивацию пространства
          отобьёт. Разрушительной кнопки в этом окне нет вовсе: `Confirm`
          показал бы «Удалить клиента», человек нажал бы, получил бы 409 — и
          остался бы с отказом вместо выхода. Здесь вместо неё выход. */}
      <Dialog
        open={removeMode === 'blocked'}
        title="У клиента есть бренды"
        onClose={() => setRemoving(null)}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setRemoving(null)}
            >
              Отмена
            </button>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={goToClientBrands}
            >
              Перейти к брендам
            </button>
          </>
        }
      >
        {removing ? <ClientHasBrandsBody client={removing} /> : null}
      </Dialog>

      {createDialog}
    </>
  );
}

/**
 * Отказ в заведении клиента.
 *
 * Текст пишет сервер — он знает и потолок тарифа, и купленный объём, — и его
 * показывает `ErrorNotice` через `humanMessage`. Здесь добавляется только то,
 * чего в тексте сервера быть не может: выход.
 *
 * Вынесено из разметки и экспортировано, потому что оба отказа приходят с одним
 * кодом ответа и означают противоположные вещи, а увидеть их глазами нельзя —
 * они бывают только у организации, упёршейся в тариф или раздавшей квоту до
 * копейки. Разбор проверяется приёмкой (`tools/check-agency-create.mjs`).
 *
 * Выход у отказа по тарифу свой у каждой роли. Заводить клиентов может и
 * аналитик, а `/billing/**` матрица открывает только владельцу: ссылка «Тариф»
 * привела бы аналитика в закрытый раздел — то есть во второй отказ подряд, уже
 * без объяснения. Поэтому ссылка показывается тому, кто до раздела дотягивается,
 * а остальным называется человек, который это решает.
 */
export function CreateRefusal({
  error,
  free,
  canOpenPlan,
  onRepeat,
}: {
  error: unknown;
  free: number;
  /** Дотягивается ли роль до раздела «Тариф» (`sections.ts`). */
  canOpenPlan: boolean;
  /** Прошлая попытка всё-таки прошла: перечитать список. */
  onRepeat: () => void;
}) {
  const refusal = refusalOf(error, free);
  return (
    <ErrorNotice
      error={error}
      action={
        refusal.kind === 'plan' ? (
          canOpenPlan ? (
            <span className="notice-act">
              <a className="btn btn-ghost btn-sm" href="/plan">
                Открыть тариф
              </a>
            </span>
          ) : (
            <span className="stamp">
              Тариф расширяет владелец организации — раздел оплаты открыт только ему.
              Попросите его перейти на старший тариф, и клиент заведётся.
            </span>
          )
        ) : refusal.kind === 'quota' ? (
          <span className="stamp">
            Свободно {countOf(refusal.free, 'вопрос', 'вопроса', 'вопросов')} — впишите
            столько или меньше. Остальное можно забрать у другого клиента.
          </span>
        ) : refusal.kind === 'repeat' ? (
          <span className="notice-act">
            <button className="btn btn-ghost btn-sm" type="button" onClick={onRepeat}>
              Обновить список
            </button>
          </span>
        ) : null
      }
    />
  );
}
